import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { InboundMessage } from 'ably';
import { pipe, filter, map } from 'remeda';
import type { Device, DeviceConnectionStatus } from '@types';
import { DEVICE_IDLE_MS, SENSOR_IDLE_MS } from '@types';
import { useAbly } from '../providers/AblyProvider';

type TimerInfo = {
  timeoutId: ReturnType<typeof setTimeout>;
  expiresAt: number;
};

interface UseDeviceConnectionsStatusReturn {
  connectionStatus: Record<string, DeviceConnectionStatus>;
  messages: Record<string, InboundMessage[]>;
  timers: Record<string, number>;
  connectDevice: (clientId: string, timeoutMs?: number) => void;
  disconnectDevice: (clientId: string) => void;
  resetDeviceTimer: (clientId: string, timeoutMs?: number) => void;
  getRemainingMs: (clientId: string) => number;
  clearAllTimers: () => void;
  checkPresenceAndConnect: (clientId: string, channel?: any) => Promise<void>;
  checkAllDevicesPresence: (channel?: any) => Promise<void>;
}

export const useDeviceConnectionsStatus = (
  devices: Device[]
): UseDeviceConnectionsStatusReturn => {
  const { subscribeToDeviceMessages } = useAbly();

  const [connectionStatus, setConnectionStatus] = useState<
    Record<string, DeviceConnectionStatus>
  >({});
  const [messages, setMessages] = useState<Record<string, InboundMessage[]>>(
    {}
  );
  const [timers, setTimers] = useState<Record<string, number>>({});

  const timeoutRegistryRef = useRef<Map<string, TimerInfo>>(new Map());
  const unsubscribeCallbacksRef = useRef<Map<string, () => void>>(new Map());

  // Extract clientIds from devices
  const deviceClientIds = useMemo(
    () =>
      pipe(
        devices,
        map(device => device.clientId ?? device.code),
        filter((id): id is string => Boolean(id))
      ),
    [devices]
  );

  // Initialize connection status from devices
  useEffect(() => {
    const initialStatus: Record<string, DeviceConnectionStatus> = {};
    deviceClientIds.forEach(clientId => {
      if (clientId) {
        initialStatus[clientId] = 'disconnected';
      }
    });
    setConnectionStatus(prev => ({ ...initialStatus, ...prev }));
  }, [deviceClientIds]);

  // Subscribe to messages for each device
  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    deviceClientIds.forEach(clientId => {
      const unsubscribe = subscribeToDeviceMessages(clientId, message => {
        // Add message to device's message list
        setMessages(prev => ({
          ...prev,
          [clientId]: [...(prev[clientId] ?? []), message],
        }));

        // Handle message types
        const payload = message.data as any;
        // Per vr-event, AblyProvider ha già fatto il routing corretto
        // quindi il callback viene chiamato solo se il clientId corrisponde
        const messageClientId =
          message.name === 'vr-event'
            ? clientId
            : payload?.clientId || message.clientId;

        // Log temporaneo per debug
        if (message.name === 'vr-event') {
          console.log(
            '[useDeviceConnectionsStatus] vr-event received in callback:',
            {
              subscribedClientId: clientId,
              messageName: message.name,
              payload,
              aboutToCallConnectDeviceInternal: true,
            }
          );
        }

        if (message.name === 'heartRate' && messageClientId === clientId) {
          connectDeviceInternal(clientId, SENSOR_IDLE_MS);
        }

        if (
          message.action === 'message.create' &&
          message.name === 'Connected' &&
          messageClientId === clientId
        ) {
          connectDeviceInternal(clientId, DEVICE_IDLE_MS);
        }

        if (message.name === 'vr-event') {
          console.log(
            '[useDeviceConnectionsStatus] Calling connectDeviceInternal for vr-event:',
            {
              clientId,
              DEVICE_IDLE_MS,
            }
          );
          connectDeviceInternal(clientId, DEVICE_IDLE_MS);
        }
      });

      unsubscribes.push(unsubscribe);
      unsubscribeCallbacksRef.current.set(clientId, unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
      unsubscribeCallbacksRef.current.clear();
    };
  }, [deviceClientIds, subscribeToDeviceMessages]);

  const connectDeviceInternal = useCallback(
    (clientId: string, timeoutMs: number = DEVICE_IDLE_MS) => {
      console.log(
        '[useDeviceConnectionsStatus] connectDeviceInternal called:',
        {
          clientId,
          timeoutMs,
          deviceClientIds,
          isInList: deviceClientIds.includes(clientId),
          currentStatus: connectionStatus[clientId],
        }
      );

      if (!clientId || !deviceClientIds.includes(clientId)) {
        console.warn(
          '[useDeviceConnectionsStatus] connectDeviceInternal skipped:',
          {
            clientId,
            reason: !clientId ? 'no clientId' : 'not in deviceClientIds',
            deviceClientIds,
          }
        );
        return;
      }

      // Update connection status
      setConnectionStatus(prev => {
        const wasConnected = prev[clientId] === 'connected';
        console.log(
          '[useDeviceConnectionsStatus] Updating connection status:',
          {
            clientId,
            wasConnected,
            willUpdate: !wasConnected,
          }
        );
        if (wasConnected) {
          return prev;
        }
        return { ...prev, [clientId]: 'connected' };
      });

      // Clear existing timer if any
      const existingInfo = timeoutRegistryRef.current.get(clientId);
      if (existingInfo) {
        clearTimeout(existingInfo.timeoutId);
        timeoutRegistryRef.current.delete(clientId);
      }

      // Set new timer
      const expiresAt = Date.now() + timeoutMs;
      const timeoutId = setTimeout(() => {
        disconnectDeviceInternal(clientId);
      }, timeoutMs);

      timeoutRegistryRef.current.set(clientId, { timeoutId, expiresAt });
      setTimers(prev => ({ ...prev, [clientId]: expiresAt }));

      console.log('[useDeviceConnectionsStatus] Timer set for device:', {
        clientId,
        timeoutMs,
        expiresAt: new Date(expiresAt).toISOString(),
        expiresIn: timeoutMs,
      });
    },
    [deviceClientIds, connectionStatus]
  );

  const disconnectDeviceInternal = useCallback((clientId: string) => {
    console.log(
      '[useDeviceConnectionsStatus] disconnectDeviceInternal called:',
      {
        clientId,
        hadTimer: timeoutRegistryRef.current.has(clientId),
      }
    );

    const info = timeoutRegistryRef.current.get(clientId);
    if (info) {
      clearTimeout(info.timeoutId);
      timeoutRegistryRef.current.delete(clientId);
    }

    setConnectionStatus(prev => {
      console.log('[useDeviceConnectionsStatus] Disconnecting device:', {
        clientId,
        previousStatus: prev[clientId],
      });
      return {
        ...prev,
        [clientId]: 'disconnected',
      };
    });

    setTimers(prev => {
      const { [clientId]: _unused, ...remaining } = prev;
      void _unused; // Explicitly mark as intentionally unused
      return remaining;
    });
  }, []);

  const connectDevice = useCallback(
    (clientId: string, timeoutMs?: number) => {
      connectDeviceInternal(clientId, timeoutMs);
    },
    [connectDeviceInternal]
  );

  const disconnectDevice = useCallback(
    (clientId: string) => {
      disconnectDeviceInternal(clientId);
    },
    [disconnectDeviceInternal]
  );

  const resetDeviceTimer = useCallback(
    (clientId: string, timeoutMs: number = DEVICE_IDLE_MS) => {
      if (!clientId || !deviceClientIds.includes(clientId)) {
        return;
      }

      const currentStatus = connectionStatus[clientId];
      if (currentStatus !== 'connected') {
        return;
      }

      const existingInfo = timeoutRegistryRef.current.get(clientId);
      if (existingInfo) {
        clearTimeout(existingInfo.timeoutId);
      }

      const expiresAt = Date.now() + timeoutMs;
      const timeoutId = setTimeout(() => {
        disconnectDeviceInternal(clientId);
      }, timeoutMs);

      timeoutRegistryRef.current.set(clientId, { timeoutId, expiresAt });
      setTimers(prev => ({ ...prev, [clientId]: expiresAt }));
    },
    [deviceClientIds, connectionStatus, disconnectDeviceInternal]
  );

  const getRemainingMs = useCallback(
    (clientId: string): number => {
      const expiresAt = timers[clientId];
      return expiresAt ? Math.max(0, expiresAt - Date.now()) : 0;
    },
    [timers]
  );

  const clearAllTimers = useCallback(() => {
    timeoutRegistryRef.current.forEach(({ timeoutId }) => {
      clearTimeout(timeoutId);
    });
    timeoutRegistryRef.current.clear();
    setTimers({});
  }, []);

  const checkPresenceAndConnect = useCallback(
    async (clientId: string, channel?: any): Promise<void> => {
      if (!clientId || !deviceClientIds.includes(clientId)) {
        return;
      }

      const currentStatus = connectionStatus[clientId];
      if (currentStatus === 'connected') {
        return;
      }

      try {
        if (channel?.presence?.get) {
          const members = await channel.presence.get();
          const isPresent = members.some(
            (member: any) => member.clientId === clientId
          );
          if (isPresent) {
            connectDeviceInternal(clientId);
          }
        } else {
          connectDeviceInternal(clientId);
        }
      } catch {
        connectDeviceInternal(clientId);
      }
    },
    [deviceClientIds, connectionStatus, connectDeviceInternal]
  );

  const checkAllDevicesPresence = useCallback(
    async (channel?: any): Promise<void> => {
      const devicesToCheck = pipe(
        deviceClientIds,
        filter(clientId => connectionStatus[clientId] !== 'connected')
      );

      if (devicesToCheck.length === 0) {
        return;
      }

      const presenceChecks = pipe(
        devicesToCheck,
        map(clientId => checkPresenceAndConnect(clientId, channel))
      );

      await Promise.allSettled(presenceChecks);
    },
    [deviceClientIds, connectionStatus, checkPresenceAndConnect]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutRegistryRef.current.forEach(({ timeoutId }) => {
        clearTimeout(timeoutId);
      });
      timeoutRegistryRef.current.clear();
      unsubscribeCallbacksRef.current.forEach(unsub => unsub());
      unsubscribeCallbacksRef.current.clear();
    };
  }, []);

  return {
    connectionStatus,
    messages,
    timers,
    connectDevice,
    disconnectDevice,
    resetDeviceTimer,
    getRemainingMs,
    clearAllTimers,
    checkPresenceAndConnect,
    checkAllDevicesPresence,
  };
};
